/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { getAxios } from '@/lib/axios';
import {
  type AcesDealsVehicleAttributes,
  type DealListData,
  type DealListResponse,
} from '../interface';
import { requestBaseURL } from '@/config/serviceAPI';
import { type AxiosInstance } from 'axios';
import { createQuery } from '@/utils/createReactQuery';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useAcesDealsVehicleAttributes } from '../hooks/useAcesDealsVehicleAttributes';

type ApiVersion = 'v3' | 'v4';

type OptionsV3 = {
  apiVersion: 'v3';
  skuId: string | undefined;
};
type OptionsV4 = {
  apiVersion: 'v4';
  skuId: string | undefined;
  dealsVehicleAttributes: AcesDealsVehicleAttributes | null;
};

type Options = OptionsV3 | OptionsV4;

const urlByVersion: Record<ApiVersion, string> = {
  v3: `${requestBaseURL}/ecomm/b2c/browse/v3/skus`,
  v4: `${requestBaseURL}/ecomm/b2c/browse/v4/skus`,
};

export const getDealList = async (options: Options, axiosInstance?: AxiosInstance) => {
  const { skuId, apiVersion: version } = options;
  const URL = urlByVersion[version];
  const params =
    options.apiVersion === 'v4' && options.dealsVehicleAttributes
      ? { ...options.dealsVehicleAttributes }
      : undefined;

  if (!skuId) {
    throw new Error('skuId is required');
  }

  const response = await getAxios(axiosInstance).get<DealListResponse>(`${URL}/${skuId}/deals`, {
    params,
  });

  return selector(response.data);
};

const selector = (data: DealListResponse): DealListData => data;

const { useData, query: dealListQuery } = createQuery<DealListResponse, Options>(
  'dealList',
  getDealList
);

type PublicOptions = { skuId: string | undefined; enabled?: boolean };

export const useDealList = ({ enabled = true, skuId }: PublicOptions) => {
  const { acesEnabled } = useAcesFlags();
  const dealsVehicleAttributes = useAcesDealsVehicleAttributes();

  const options: Options = {
    apiVersion: acesEnabled ? 'v4' : 'v3',
    skuId,
    dealsVehicleAttributes,
  };

  return useData({
    enabled: enabled && Boolean(skuId),
    ...options,
  });
};

export { dealListQuery };
